import type { SettingsOption } from '../types/settings-option'

export const DEFAULT_TIMEZONE = 'UTC'

const FALLBACK_TIMEZONE_OPTIONS: SettingsOption[] = [
  { label: '(UTC -12) Baker Island', value: 'Etc/GMT+12' },
  { label: '(UTC -11) Pago Pago, Niue', value: 'Pacific/Pago_Pago' },
  { label: '(UTC -10) Honolulu, Tahiti', value: 'Pacific/Honolulu' },
  { label: '(UTC -9) Anchorage, Gambier', value: 'America/Anchorage' },
  { label: '(UTC -8) Los Angeles, Vancouver, Tijuana', value: 'America/Los_Angeles' },
  { label: '(UTC -7) Denver, Phoenix, Calgary', value: 'America/Denver' },
  { label: '(UTC -6) Chicago, Mexico City, Winnipeg', value: 'America/Chicago' },
  { label: '(UTC -5) New York, Toronto, Lima', value: 'America/New_York' },
  { label: '(UTC -4) Santiago, Halifax, Caracas', value: 'America/Santiago' },
  { label: '(UTC -3) Buenos Aires, Sao Paulo, Montevideo', value: 'America/Sao_Paulo' },
  { label: '(UTC -2) South Georgia', value: 'Atlantic/South_Georgia' },
  { label: '(UTC -1) Azores, Cape Verde', value: 'Atlantic/Azores' },
  { label: '(UTC +0) UTC, London, Lisbon, Dublin', value: DEFAULT_TIMEZONE },
  { label: '(UTC +1) Berlin, Paris, Madrid, Lagos', value: 'Europe/Berlin' },
  { label: '(UTC +2) Cairo, Athens, Johannesburg, Helsinki', value: 'Europe/Athens' },
  { label: '(UTC +3) Moscow, Istanbul, Riyadh, Nairobi', value: 'Europe/Moscow' },
  { label: '(UTC +4) Dubai, Baku, Tbilisi', value: 'Asia/Dubai' },
  { label: '(UTC +5) Karachi, Tashkent', value: 'Asia/Karachi' },
  { label: '(UTC +5:30) New Delhi, Mumbai, Colombo', value: 'Asia/Kolkata' },
  { label: '(UTC +6) Dhaka, Almaty', value: 'Asia/Dhaka' },
  { label: '(UTC +7) Bangkok, Jakarta, Ho Chi Minh City', value: 'Asia/Bangkok' },
  { label: '(UTC +8) Perth, Beijing, Shanghai', value: 'Asia/Shanghai' },
  { label: '(UTC +9) Tokyo, Seoul, Osaka', value: 'Asia/Tokyo' },
  { label: '(UTC +9:30) Adelaide, Darwin', value: 'Australia/Adelaide' },
  { label: '(UTC +10) Sydney, Melbourne, Brisbane', value: 'Australia/Sydney' },
  { label: '(UTC +11) Solomon Islands, Noumea', value: 'Pacific/Guadalcanal' },
  { label: '(UTC +12) Auckland, Fiji, Wellington', value: 'Pacific/Auckland' },
  { label: "(UTC +13) Nuku'alofa, Samoa", value: 'Pacific/Tongatapu' },
  { label: '(UTC +14) Kiritimati', value: 'Pacific/Kiritimati' }
]

type IntlWithSupportedValues = typeof Intl & {
  supportedValuesOf?: (key: 'timeZone') => string[]
}

function getTimezoneNames(): string[] | undefined {
  const supportedValuesOf = (Intl as IntlWithSupportedValues).supportedValuesOf
  if (!supportedValuesOf) return

  const timezones = supportedValuesOf('timeZone')
  if (timezones.length === 0) return

  // UTC is a valid IANA timezone, but it is not returned by supportedValuesOf.
  const names = new Set([DEFAULT_TIMEZONE, ...timezones])

  // Keep the browser timezone selectable in environments without supportedValuesOf.
  try {
    names.add(Intl.DateTimeFormat().resolvedOptions().timeZone)
  } catch {
    // Intl can be partially implemented in embedded browsers.
  }

  return [...names]
}

function getUtcOffsetMinutes(timezone: string): number {
  const timezoneName = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'longOffset'
  })
    .formatToParts()
    .find((part) => part.type === 'timeZoneName')?.value

  const match = timezoneName?.match(/^GMT(?:([+-])(\d{1,2})(?::(\d{2}))?)?$/)
  if (!match?.[1]) return 0

  const offset = Number(match[2]) * 60 + Number(match[3] ?? 0)
  return match[1] === '-' ? -offset : offset
}

function formatUtcOffset(offsetMinutes: number): string {
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const absoluteOffset = Math.abs(offsetMinutes)
  const hours = Math.floor(absoluteOffset / 60)
    .toString()
    .padStart(2, '0')
  const minutes = (absoluteOffset % 60).toString().padStart(2, '0')

  return `UTC${sign}${hours}:${minutes}`
}

function getFallbackTimezoneOptions(): SettingsOption[] {
  const options = [...FALLBACK_TIMEZONE_OPTIONS]

  try {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const isAlreadyIncluded = options.some(({ value }) => value === browserTimezone)

    if (!isAlreadyIncluded) {
      const offset = getUtcOffsetMinutes(browserTimezone)
      options.push({
        label: `(${formatUtcOffset(offset)}) ${browserTimezone.replaceAll('_', ' ')}`,
        value: browserTimezone
      })
    }
  } catch {
    // The hardcoded list is still usable if timezone detection is unavailable.
  }

  return options
}

export function createTimezoneOptions(): SettingsOption[] {
  try {
    const timezoneNames = getTimezoneNames()
    if (!timezoneNames) return getFallbackTimezoneOptions()

    return timezoneNames
      .map((timezone) => {
        const offset = getUtcOffsetMinutes(timezone)

        return {
          label: `(${formatUtcOffset(offset)}) ${timezone.replaceAll('_', ' ')}`,
          value: timezone,
          offset
        }
      })
      .sort((left, right) => left.offset - right.offset || left.value.localeCompare(right.value))
      .map(({ label, value }) => ({ label, value }))
  } catch {
    return getFallbackTimezoneOptions()
  }
}

export const TIMEZONE_OPTIONS: SettingsOption[] = createTimezoneOptions()

export const CURRENCY_OPTIONS: SettingsOption[] = [
  { label: 'USD', value: 'USD' },
  { label: 'EUR', value: 'EUR' },
  { label: 'GBP', value: 'GBP' },
  { label: 'JPY', value: 'JPY' },
  // { label: 'CNY', value: 'CNY' }, // Нет доступного FX-курса
  // { label: 'RUB', value: 'RUB' }, // Нет доступного FX-курса
  // { label: 'AED', value: 'AED' }, // Нет доступного FX-курса
  { label: 'CHF', value: 'CHF' },
  { label: 'AUD', value: 'AUD' },
  { label: 'CAD', value: 'CAD' }
]

export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  RUB: '₽',
  AED: 'د.إ',
  CHF: 'CHF',
  AUD: 'A$',
  CAD: 'C$'
}

export function getCurrencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency.toUpperCase()] ?? currency
}
